// DuplicateFilesPrompt.js
// A file added to a selection that matches one already there on name, size and
// last-modified time is probably the same file picked twice — the pickers reset
// after every pick, so that is easy to do. It can also be a different file that
// happens to share the metadata, and two picks must then be two uploads. The
// selector cannot tell the two apart, so the user decides: the look-alikes are
// held here for Keep / Discard instead of being dropped silently.
import React, { useRef } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

export const fileIdentityKey = (file) => `${file.name}|${file.size}|${file.lastModified}`;

// Split newly accepted files into those unlike anything selected (`fresh`) and
// those that look like a file already in the selection (`likely`).
export function splitLikelyDuplicates(existing, incoming) {
  const existingKeys = new Set(existing.map(fileIdentityKey));
  const fresh = [];
  const likely = [];
  incoming.forEach((file) => {
    (existingKeys.has(fileIdentityKey(file)) ? likely : fresh).push(file);
  });
  return { fresh, likely };
}

function DuplicateFilesPrompt({ files, onKeep, onDiscard }) {
  const open = Boolean(files && files.length > 0);
  // The dialog stays mounted through its closing fade after `files` is cleared,
  // so the text keeps naming the last files rather than flipping to an empty list.
  const shownRef = useRef([]);
  if (open) shownRef.current = files;
  const names = shownRef.current.map((file) => file.name);
  const single = names.length === 1;
  return (
    <Dialog open={open} onClose={onDiscard} maxWidth="xs" fullWidth>
      <DialogTitle>Already added?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {single
            ? `"${names[0]}" looks like it was added twice — same name, size and last-modified time.`
            : `These files look like they were added twice — same name, size and last-modified time: ${names.join(', ')}.`}
          {single ? ' Keep it as well, or discard it?' : ' Keep them as well, or discard them?'}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onDiscard}>Discard</Button>
        <Button variant="contained" onClick={onKeep}>Keep</Button>
      </DialogActions>
    </Dialog>
  );
}

export default DuplicateFilesPrompt;
